import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Search, 
  Filter, 
  MoreHorizontal,
  Edit,
  Trash2,
  Eye,
  Tag,
  Calendar,
  DollarSign,
  TrendingUp,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  BarChart3,
  X,
  Check,
  Heart,
  ChevronDown,
  ChevronRight
} from 'lucide-react';
import PieceCard from '../components/PieceCard';
import { useWallet } from '../contexts/WalletContext';
import { useFavorites } from '../hooks/useFavorites';
import WalletRequired from '../components/WalletRequired';
import ModelViewer from '../components/ModelViewer.jsx';
import PieceActivityAndHolders from '../components/PieceActivityAndHolders';
import VerifiedBadge from '../components/VerifiedBadge';
import { normalizePieces } from '../utils/pieceNormalizer.js';
import { getCategoryDisplayName } from '../utils/pieceUtils';
import { useSolPrice } from '../contexts/SolPriceContext';
import {
  getWalletOffers,
  acceptOffer,
  declineOffer,
  cancelOffer,
  updateOffer,
  formatTimeRemaining,
  completePurchase,
  checkAndDeclineExpiredOffers,
} from '../utils/offersContract';

const Offers = () => {
  const navigate = useNavigate();
  const { isConnected, balance, publicKey, wallet } = useWallet();
  const { toggleFavorite, isFavorited } = useFavorites();
  const { solPrice } = useSolPrice();
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('incoming'); // 'incoming', 'requested', 'history'
  const [sortBy, setSortBy] = useState('recent');
  const [acceptModal, setAcceptModal] = useState({ isOpen: false, offer: null });
  const [declineModal, setDeclineModal] = useState({ isOpen: false, offer: null });
  const [viewDetailsModal, setViewDetailsModal] = useState({ isOpen: false, offer: null });
  const [editPriceModal, setEditPriceModal] = useState({ isOpen: false, offer: null, newPrice: '' });
  const [cancelOfferModal, setCancelOfferModal] = useState({ isOpen: false, offer: null });
  const [confirmPurchaseModal, setConfirmPurchaseModal] = useState({ isOpen: false, offer: null });
  const [isSortDropdownOpen, setIsSortDropdownOpen] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [modalViewMode, setModalViewMode] = useState('3d'); // '3d' or 'thumbnail'
  const [threeDStatus, setThreeDStatus] = useState('loading'); // 'loading', 'loaded', 'error'
  const [threeDError, setThreeDError] = useState(null);
  const [activityPanelOpen, setActivityPanelOpen] = useState(false);
  const [notification, setNotification] = useState({ show: false, type: 'success', message: '' });
  const sortDropdownRef = useRef(null);

  // Helper function to show notifications
  const showNotification = (message, type = 'success') => {
    setNotification({ show: true, message, type });
    setTimeout(() => {
      setNotification({ show: false, message: '', type: 'success' });
    }, 5000);
  };

  useEffect(() => {
    if (isConnected && publicKey) {
      loadOffers();
    } else {
      setOffers([]);
      setLoading(false);
    }
  }, [isConnected, publicKey]);

  // Auto-refresh offers every 60 seconds to check for expired deadlines
  useEffect(() => {
    if (!isConnected || !publicKey) return;
    
    const refreshInterval = setInterval(() => {
      console.log('🔄 Auto-refreshing offers to check for expired deadlines...');
      loadOffers();
    }, 60000); // Refresh every 60 seconds

    return () => clearInterval(refreshInterval);
  }, [isConnected, publicKey]);

  // Handle click outside dropdowns
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (sortDropdownRef.current && !sortDropdownRef.current.contains(event.target)) {
        setIsSortDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Sort options
  const sortOptions = [
    { id: 'recent', name: 'Most Recent' },
    { id: 'price-low', name: 'Price: Low to High' },
    { id: 'price-high', name: 'Price: High to Low' },
    { id: 'expires', name: 'Expires Soon' }
  ];

  // Handle dropdown changes
  const handleSortChange = (optionId) => {
    setSortBy(optionId);
    setIsSortDropdownOpen(false);
  };

  const loadOffers = async () => {
    if (!publicKey) {
      setOffers([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const walletAddr = publicKey.toString();
      console.log('📥 Loading offers for wallet:', walletAddr);
      
      // First, check and auto-decline any expired accepted offers
      const declineResult = await checkAndDeclineExpiredOffers();
      if (declineResult.success && declineResult.declined_count > 0) {
        console.log(`⚠️ Auto-declined ${declineResult.declined_count} expired offer(s)`);
        showNotification(
          `${declineResult.declined_count} accepted offer(s) expired and were automatically declined`,
          'info'
        );
      }
      
      // Get all offers for this wallet (both made and received)
      const result = await getWalletOffers(walletAddr, 'all');
      
      if (!result.success) {
        console.error('❌ Failed to load offers:', result.error);
        setOffers([]);
        return;
      }

      console.log(`✅ Loaded ${result.offers.length} offers from database`);
      
      if (result.offers.length > 0) {
        console.log('📋 Offers breakdown:');
        const made = result.offers.filter(o => o.offerer_wallet === walletAddr);
        const received = result.offers.filter(o => o.piece_owner_wallet === walletAddr);
        console.log(`   - Made by you: ${made.length}`);
        console.log(`   - Received by you: ${received.length}`);
        result.offers.forEach((o, i) => {
          const type = o.offerer_wallet === walletAddr ? 'MADE' : 'RECEIVED';
          console.log(`   ${i+1}. [${type}] ${o.piece_name} - ${o.offer_price} SOL (${o.status})`);
        });
      } else {
        console.log('ℹ️ No offers found for this wallet');
        console.log('   Try making an offer on any piece to see it here!');
      }
      
      // Format offers for display
      const formattedOffers = result.offers.map(offer => {
        // Determine if this is a received or made offer
        const isReceived = offer.piece_owner_wallet === publicKey.toString();
        
        // Log creator and piece information for debugging
        console.log('📊 Offer data received:', {
          offerId: offer.id,
          pieceName: offer.piece_name,
          creator: offer.creator,
          originalCreator: offer.originalCreator,
          piece_description: offer.piece_description,
          piece_color: offer.piece_color,
          piece_tags: offer.piece_tags,
          piece_price: offer.piece_price,
          piece_listing_price: offer.piece_listing_price,
          royalty_bps: offer.royalty_bps,
        });
        
        // For accepted offers, show purchase deadline instead of offer expiration
        const isAccepted = offer.status === 'accepted';
        const deadlineTime = isAccepted && offer.accepted_expires_at ? offer.accepted_expires_at : offer.expires_at;
        const timeLeftText = formatTimeRemaining(deadlineTime);
        
        return {
          id: offer.id,
          type: isReceived ? 'received' : 'made',
          name: offer.piece_name,
          description: `Offer on ${offer.piece_name}`,
          category: offer.piece_category || 'Other',
          color: '',
          price: offer.offer_price.toString(),
          floorPrice: offer.listing_price?.toString() || offer.floor_price?.toString() || '0',
          status: offer.status,
          offerFrom: isReceived ? offer.offerer_wallet.slice(0, 6) : undefined,
          offerTo: !isReceived ? offer.piece_owner_wallet.slice(0, 6) : undefined,
          timeLeft: timeLeftText,
          timeLeftLabel: isAccepted ? 'Purchase deadline' : 'Offer expires',
          expiresAt: offer.expires_at,
          acceptedExpiresAt: offer.accepted_expires_at,
          deadlineTime: deadlineTime,
          createdDate: offer.created_at,
          message: offer.message,
          thumbnail: offer.piece_image_url ? { dataUrl: offer.piece_image_url } : null,
          thumbnailUrl: offer.piece_thumbnail_url || offer.piece_image_url,
          // Store full wallet addresses for actions
          offerer_wallet: offer.offerer_wallet,
          piece_owner_wallet: offer.piece_owner_wallet,
          mint_address: offer.mint_address,
          piece_id: offer.piece_id,
          // Creator information
          creator: offer.creator,
          originalCreator: offer.originalCreator,
          // Offerer and Owner profiles
          offererProfile: offer.offererProfile,
          ownerProfile: offer.ownerProfile,
          // Piece details
          piece_description: offer.piece_description,
          piece_brand: offer.piece_brand,
          piece_color: offer.piece_color,
          piece_rarity: offer.piece_rarity,
          piece_quantity: offer.piece_quantity,
          piece_total_made: offer.piece_total_made,
          piece_tags: offer.piece_tags,
          piece_price: offer.piece_price,
          piece_listing_price: offer.piece_listing_price,
          piece_created_at: offer.piece_created_at,
          // Royalty information
          royalty_bps: offer.royalty_bps !== undefined ? offer.royalty_bps : null,
          // 3D Model information
          preview_url: offer.piece_preview_url,
          previewUrl: offer.piece_preview_url, // normalized name
          preview_bucket: offer.preview_bucket,
          preview_key: offer.preview_key,
          model_url: offer.model_url,
          modelUrl: offer.model_url, // normalized name
          model_bucket: offer.model_bucket,
          model_key: offer.model_key,
        };
      });

      setOffers(formattedOffers);
    } catch (error) {
      console.error('Error loading offers:', error);
      setOffers([]);
    } finally {
      setLoading(false);
    }
  };

  // Filter offers based on active tab
  const getFilteredOffers = () => {
    let filtered = offers;

    // Filter by tab
    switch (activeTab) {
      case 'requested':
        // Offers made by user that are active or accepted (so buyer can complete purchase)
        filtered = filtered.filter(offer => offer.type === 'made' && (offer.status === 'active' || offer.status === 'accepted'));
        break;
      case 'incoming':
        // Offers received by user that are active or accepted
        filtered = filtered.filter(offer => offer.type === 'received' && (offer.status === 'active' || offer.status === 'accepted'));
        break;
      case 'history':
        // All completed, expired, declined, cancelled, or executed offers
        filtered = filtered.filter(offer => 
          offer.status === 'expired' || 
          offer.status === 'declined' || 
          offer.status === 'cancelled' ||
          offer.status === 'completed' ||
          offer.status === 'executed'
        );
        break;
      default:
        break;
    }

    // Filter by search term
    if (searchTerm.trim()) {
      filtered = filtered.filter(offer => 
        offer.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        offer.category.toLowerCase().includes(searchTerm.toLowerCase()) ||
        offer.description.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Sort offers
    switch (sortBy) {
      case 'recent':
        filtered.sort((a, b) => new Date(b.createdDate) - new Date(a.createdDate));
        break;
      case 'price-low':
        filtered.sort((a, b) => parseFloat(a.price) - parseFloat(b.price));
        break;
      case 'price-high':
        filtered.sort((a, b) => parseFloat(b.price) - parseFloat(a.price));
        break;
      case 'expires':
        filtered.sort((a, b) => new Date(a.expiresAt) - new Date(b.expiresAt));
        break;
      default:
        break;
    }

    return filtered;
  };

  // Get status icon and color
  const getStatusInfo = (status) => {
    switch (status) {
      case 'active':
        return { icon: <Clock size={16} />, color: 'text-green-400', bg: 'bg-green-500/10', label: 'Active' };
      case 'expired':
        return { icon: <XCircle size={16} />, color: 'text-gray-400', bg: 'bg-gray-500/10', label: 'Expired' };
      case 'accepted':
        return { icon: <CheckCircle size={16} />, color: 'text-blue-400', bg: 'bg-blue-500/10', label: 'Accepted' };
      case 'declined':
        return { icon: <AlertCircle size={16} />, color: 'text-red-400', bg: 'bg-red-500/10', label: 'Declined' };
      case 'cancelled':
        return { icon: <XCircle size={16} />, color: 'text-red-400', bg: 'bg-red-500/10', label: 'Cancelled' };
      case 'executed':
        return { icon: <CheckCircle size={16} />, color: 'text-green-400', bg: 'bg-green-500/10', label: 'Completed' };
      case 'completed':
        return { icon: <CheckCircle size={16} />, color: 'text-green-400', bg: 'bg-green-500/10', label: 'Completed' };
      default:
        return { icon: <Clock size={16} />, color: 'text-gray-400', bg: 'bg-gray-500/10', label: 'Unknown' };
    }
  };

  // Use imported formatTimeRemaining function

  // Handle offer actions
  const handleAcceptOffer = (offer) => {
    if (offer.status !== 'active') {
      showNotification('Cannot accept inactive offers', 'error');
      return;
    }
    setAcceptModal({ isOpen: true, offer });
  };

  const confirmAcceptOffer = async () => {
    if (!wallet) {
      showNotification('Please connect your wallet', 'error');
      return;
    }

    const { offer } = acceptModal;
    setIsProcessing(true);

    try {
      console.log('✅ Accepting offer:', offer.id);
      
      // This will:
      // 1. Set delegate approval for the NFT
      // 2. Call edge function to accept offer
      // 3. Prepare transaction for buyer to sign
      const result = await acceptOffer(wallet, offer.id);
      
      if (!result.success) {
        throw new Error(result.error || 'Failed to accept offer');
      }

      console.log('✅ Offer accepted successfully');
      
      // Update local state immediately
      setOffers(prevOffers => 
        prevOffers.map(o => 
          o.id === offer.id 
            ? { ...o, status: 'accepted' }
            : o
        )
      );

      // Close modal first
      setAcceptModal({ isOpen: false, offer: null });

      showNotification(`Offer accepted! The buyer has been notified and can now complete the purchase of ${offer.name} for ${offer.price} SOL.`, 'success');
      
      // Don't reload - local state is already updated correctly
      console.log('💡 Offer accepted. Local state updated.');
    } catch (error) {
      console.error('Error accepting offer:', error);
      showNotification(`Failed to accept offer: ${error.message}`, 'error');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDeclineOffer = (offer) => {
    if (offer.status !== 'active') {
      showNotification('Cannot decline inactive offers', 'error');
      return;
    }
    setDeclineModal({ isOpen: true, offer });
  };

  const confirmDeclineOffer = async () => {
    if (!wallet) {
      showNotification('Please connect your wallet', 'error');
      return;
    }

    const { offer } = declineModal;
    setIsProcessing(true);

    try {
      console.log('❌ Declining offer:', offer.id);
      
      const result = await declineOffer(wallet, offer.id, 'Not interested at this time');
      
      if (!result.success) {
        throw new Error(result.error || 'Failed to decline offer');
      }

      console.log('✅ Offer declined');
      
      // Update local state immediately
      setOffers(prevOffers => 
        prevOffers.map(o => 
          o.id === offer.id 
            ? { ...o, status: 'declined' }
            : o
        )
      );

      // Close modal
      setDeclineModal({ isOpen: false, offer: null });

      showNotification('Offer declined successfully', 'success');
      
      // Don't reload - local state is already updated correctly
      console.log('💡 Offer declined. Local state updated.');
    } catch (error) {
      console.error('Error declining offer:', error);
      showNotification(`Failed to decline offer: ${error.message}`, 'error');
    } finally {
      setIsProcessing(false);
    }
  };

  // Handle edit price
  const handleEditPrice = (offer) => {
    if (offer.status !== 'active') {
      showNotification('Cannot edit inactive offers', 'error');
      return;
    }
    setEditPriceModal({ isOpen: true, offer, newPrice: offer.price });
  };

  const confirmEditPrice = async () => {
    if (!wallet) {
      showNotification('Please connect your wallet', 'error');
      return;
    }

    const { offer, newPrice } = editPriceModal;
    if (!newPrice || parseFloat(newPrice) <= 0) {
      showNotification('Please enter a valid price', 'error');
      return;
    }

    setIsProcessing(true);

    try {
      console.log('✏️ Updating offer price:', offer.id);
      
      const result = await updateOffer(wallet, offer.id, parseFloat(newPrice));
      
      if (!result.success) {
        throw new Error(result.error || 'Failed to update offer');
      }

      console.log('✅ Offer updated');
      showNotification('Offer price updated successfully', 'success');
      
      // Reload offers
      await loadOffers();
      
      // Close modal
      setEditPriceModal({ isOpen: false, offer: null, newPrice: '' });
    } catch (error) {
      console.error('Error updating offer:', error);
      showNotification(`Failed to update offer: ${error.message}`, 'error');
    } finally {
      setIsProcessing(false);
    }
  };

  // Handle cancel offer
  const handleCancelOffer = (offer) => {
    if (offer.status !== 'active') {
      showNotification('Cannot cancel inactive offers', 'error');
      return;
    }
    setCancelOfferModal({ isOpen: true, offer });
  };

  const confirmCancelOffer = async () => {
    if (!wallet) {
      showNotification('Please connect your wallet', 'error');
      return;
    }

    const { offer } = cancelOfferModal;
    setIsProcessing(true);

    try {
      console.log('❌ Cancelling offer:', offer.id);
      
      const result = await cancelOffer(wallet, offer.id, 'Changed my mind');
      
      if (!result.success) {
        throw new Error(result.error || 'Failed to cancel offer');
      }

      console.log('✅ Offer cancelled');
      
      // Update local state immediately
      setOffers(prevOffers => 
        prevOffers.map(o => 
          o.id === offer.id 
            ? { ...o, status: 'cancelled' }
            : o
        )
      );

      // Close modal
      setCancelOfferModal({ isOpen: false, offer: null });

      showNotification('Offer cancelled successfully', 'success');
      
      // Don't reload - local state is already updated correctly
      console.log('💡 Offer cancelled. Local state updated.');
    } catch (error) {
      console.error('Error cancelling offer:', error);
      showNotification(`Failed to cancel offer: ${error.message}`, 'error');
    } finally {
      setIsProcessing(false);
    }
  };

  // Handle complete purchase
  const handleCompletePurchase = (offer) => {
    if (!wallet || !isConnected) {
      showNotification('Please connect your wallet', 'error');
      return;
    }

    if (offer.status !== 'accepted') {
      showNotification('This offer has not been accepted yet', 'error');
      return;
    }

    // Show confirmation modal instead of browser alert
    setConfirmPurchaseModal({ isOpen: true, offer });
  };

  const confirmCompletePurchase = async () => {
    const { offer } = confirmPurchaseModal;
    setIsProcessing(true);

    try {
      console.log('💰 Completing purchase...');
      
      const result = await completePurchase(wallet, offer.id);

      if (!result.success) {
        throw new Error(result.error || 'Failed to complete purchase');
      }

      console.log('✅ Purchase completed!');
      
      // Update local state immediately so UI updates right away
      setOffers(prevOffers => 
        prevOffers.map(o => 
          o.id === offer.id 
            ? { ...o, status: 'executed' }
            : o
        )
      );

      // Close modals
      setConfirmPurchaseModal({ isOpen: false, offer: null });
      closeViewDetailsModal();

      showNotification(
        `✅ Purchase Successful! You bought ${offer.name} for ${offer.price} SOL. Transaction: ${result.signature.slice(0, 8)}...`,
        'success'
      );

      // Don't reload offers automatically - the local state is already updated correctly
      // User can manually refresh if they want to see other changes
      console.log('💡 Purchase complete. Local state updated. Offer moved to history.');

    } catch (error) {
      console.error('Error completing purchase:', error);
      showNotification(`Failed to complete purchase: ${error.message}`, 'error');
    } finally {
      setIsProcessing(false);
    }
  };

  // Handle view details
  const handleViewDetails = (offer) => {
    console.log('🔍 Opening offer details:', {
      offerId: offer.id,
      offerName: offer.name,
      creator: offer.creator,
      originalCreator: offer.originalCreator,
      fullOffer: offer
    });
    
    // Normalize the piece data to ensure verificationType is correctly mapped
    // But preserve all the original offer fields
    const normalizedPiece = offer ? normalizePieces([offer])[0] : null;
    const enrichedOffer = {
      ...normalizedPiece,
      // Preserve all original offer-specific fields
      type: offer.type,
      status: offer.status,
      price: offer.price,
      floorPrice: offer.floorPrice,
      offerFrom: offer.offerFrom,
      offerTo: offer.offerTo,
      timeLeft: offer.timeLeft,
      expiresAt: offer.expiresAt,
      createdDate: offer.createdDate,
      message: offer.message,
      offerer_wallet: offer.offerer_wallet,
      piece_owner_wallet: offer.piece_owner_wallet,
      mint_address: offer.mint_address,
      piece_id: offer.piece_id,
      // Preserve offer profiles
      offererProfile: offer.offererProfile,
      ownerProfile: offer.ownerProfile,
      // Preserve piece details with piece_ prefix
      piece_description: offer.piece_description,
      piece_brand: offer.piece_brand,
      piece_color: offer.piece_color,
      piece_rarity: offer.piece_rarity,
      piece_quantity: offer.piece_quantity,
      piece_total_made: offer.piece_total_made,
      piece_tags: offer.piece_tags,
      piece_price: offer.piece_price,
      piece_listing_price: offer.piece_listing_price,
      piece_created_at: offer.piece_created_at,
      // Preserve royalty info
      royalty_bps: offer.royalty_bps !== undefined ? offer.royalty_bps : normalizedPiece?.royalty_bps,
      // Preserve 3D model info
      preview_url: offer.preview_url,
      previewUrl: offer.previewUrl || offer.preview_url || normalizedPiece?.previewUrl,
      preview_bucket: offer.preview_bucket,
      preview_key: offer.preview_key,
      model_url: offer.model_url,
      modelUrl: offer.modelUrl || offer.model_url || normalizedPiece?.modelUrl,
      model_bucket: offer.model_bucket,
      model_key: offer.model_key,
      // Ensure thumbnail is preserved
      thumbnail: offer.thumbnail,
      thumbnailUrl: offer.thumbnailUrl || normalizedPiece?.thumbnailUrl,
    };
    
    setViewDetailsModal({ isOpen: true, offer: enrichedOffer });
    setActivityPanelOpen(false);
    
    // Check if piece has 3D model
    const has3DModel = enrichedOffer?.previewUrl && (enrichedOffer.previewUrl.includes('.glb') || enrichedOffer.previewUrl.includes('.gltf'));
    
    if (has3DModel) {
      setModalViewMode('3d');
      setThreeDStatus('loading');
      setThreeDError(null);
    } else {
      setModalViewMode('thumbnail');
      setThreeDStatus('error');
      setThreeDError('No 3D model available');
    }
  };

  const closeViewDetailsModal = () => {
    setViewDetailsModal({ isOpen: false, offer: null });
    setActivityPanelOpen(false);
  };

  // Calculate total stats
  const getTotalStats = () => {
    const requested = offers.filter(o => o.type === 'made' && o.status === 'active').length;
    const incoming = offers.filter(o => o.type === 'received' && (o.status === 'active' || o.status === 'accepted')).length;
    const history = offers.filter(o => 
      o.status === 'expired' || 
      o.status === 'declined' || 
      o.status === 'cancelled' ||
      o.status === 'completed' ||
      o.status === 'executed'
    ).length;
    // Total value = sum of all COMPLETED sales where user was the SELLER (received offers)
    const totalValue = offers
      .filter(o => o.type === 'received' && (o.status === 'executed' || o.status === 'completed'))
      .reduce((sum, o) => sum + parseFloat(o.price || 0), 0);
    
    return { requested, incoming, history, totalValue };
  };

  const stats = getTotalStats();
  const filteredOffers = getFilteredOffers();

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-white text-xl">Loading offers...</div>
      </div>
    );
  }

  return (
    <WalletRequired 
      message="You need to connect your wallet to view and manage your offers."
      action="manage offers"
    >
      <div className="min-h-screen bg-black">
        {/* Header */}
        <div className="bg-black">
          <div className="px-6 py-6">
            <h1 className="text-3xl font-bold text-white mb-2">My Offers</h1>
            <p className="text-gray-400">Manage your marketplace offers and track their status</p>
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b border-[#2a2a2a] bg-black">
          <div className="px-6">
            <div className="flex gap-1">
              {[
                { id: 'incoming', label: 'Incoming Offers', icon: TrendingUp },
                { id: 'requested', label: 'Offers Requested', icon: Tag },
                { id: 'history', label: 'Offers History', icon: Clock }
              ].map(tab => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex items-center gap-2 px-4 py-3 text-sm font-medium transition-colors ${
                      activeTab === tab.id
                        ? 'text-white border-b-2 border-white'
                        : 'text-gray-400 hover:text-white'
                    }`}
                  >
                    <Icon size={16} />
                    {tab.label}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        <div className="p-6">
        
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-[#1a1a1a] border border-[#2a2a2a] rounded-xl p-6">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 bg-purple-500/20 rounded-full flex items-center justify-center">
                <Tag size={20} className="text-purple-500" />
              </div>
              <div>
                <div className="text-2xl font-bold text-white">{stats.requested}</div>
                <div className="text-gray-400 text-sm">Offers Requested</div>
              </div>
            </div>
          </div>

          <div className="bg-[#1a1a1a] border border-[#2a2a2a] rounded-xl p-6">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 bg-green-500/20 rounded-full flex items-center justify-center">
                <TrendingUp size={20} className="text-green-500" />
              </div>
              <div>
                <div className="text-2xl font-bold text-white">{stats.incoming}</div>
                <div className="text-gray-400 text-sm">Incoming Offers</div>
              </div>
            </div>
          </div>

          <div className="bg-[#1a1a1a] border border-[#2a2a2a] rounded-xl p-6">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 bg-blue-500/20 rounded-full flex items-center justify-center">
                <Clock size={20} className="text-blue-500" />
              </div>
              <div>
                <div className="text-2xl font-bold text-white">{stats.history}</div>
                <div className="text-gray-400 text-sm">Offers History</div>
              </div>
            </div>
          </div>

          <div className="bg-[#1a1a1a] border border-[#2a2a2a] rounded-xl p-6">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 bg-yellow-500/20 rounded-full flex items-center justify-center">
                <DollarSign size={20} className="text-yellow-500" />
              </div>
              <div>
                <div className="text-2xl font-bold text-white">{stats.totalValue.toFixed(2)} SOL</div>
                <div className="text-gray-400 text-sm">Total Value</div>
              </div>
            </div>
          </div>
        </div>

        {/* Filters and Search */}
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-6">
          <div className="flex items-center gap-4">
            <div className="relative">
              <Search size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search offers..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-64 pl-10 pr-4 py-2 bg-[#0a0a0a] border border-[#2a2a2a] rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-white"
              />
            </div>

            {/* Sort Options */}
            <div className="flex items-center gap-3">
              <span className="text-sm font-medium text-gray-300">Sort by:</span>
              <div className="relative" ref={sortDropdownRef}>
                <button
                  onClick={() => setIsSortDropdownOpen(!isSortDropdownOpen)}
                  className="flex items-center justify-between bg-[#0a0a0a] border border-[#2a2a2a] rounded-xl text-white text-sm py-3 px-4 focus:outline-none focus:ring-2 focus:ring-white focus:border-transparent transition-all duration-200 hover:border-[#3a3a3a] cursor-pointer min-w-[140px]"
                >
                  <span>{sortOptions.find(opt => opt.id === sortBy)?.name}</span>
                  <ChevronDown 
                    size={16} 
                    className={`text-gray-400 transition-transform duration-200 ${isSortDropdownOpen ? 'rotate-180' : ''}`} 
                  />
                </button>
                
                {isSortDropdownOpen && (
                  <div className="absolute top-full left-0 mt-2 w-full bg-[#0a0a0a] border border-[#2a2a2a] rounded-xl shadow-xl z-50 overflow-hidden">
                    {sortOptions.map((option) => (
                      <button
                        key={option.id}
                        onClick={() => handleSortChange(option.id)}
                        className={`w-full px-4 py-3 text-left text-sm transition-all duration-200 flex items-center justify-between ${
                          option.id === sortBy
                            ? 'bg-white/10 text-white'
                            : 'text-gray-300 hover:bg-white/5 hover:text-white'
                        }`}
                      >
                        <span>{option.name}</span>
                        {option.id === sortBy && (
                          <Check size={16} className="text-white" />
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Offers Content */}
        <div className="space-y-6">
          {filteredOffers.length > 0 ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 2xl:grid-cols-8 gap-6">
              {filteredOffers.map((offer) => {
                const statusInfo = getStatusInfo(offer.status);
                return (
                  <div 
                    key={offer.id} 
                    onClick={() => handleViewDetails(offer)}
                    className="group relative bg-[#1a1a1a] border border-white/30 rounded-xl overflow-hidden hover:border-white transition-all duration-300 hover:shadow-xl hover:shadow-white/20 cursor-pointer"
                  >
                    {/* Thumbnail/3D Model Preview Area */}
                    <div className="aspect-square bg-[#2a2a2a] flex flex-col items-center justify-center relative group-hover:bg-[#3a3a3a] transition-colors duration-200 overflow-hidden">
                      {offer.thumbnail?.dataUrl ? (
                        <img 
                          src={offer.thumbnail.dataUrl} 
                          alt={offer.name}
                          className="w-full h-full object-cover"
                        />
                      ) : offer.modelFile?.dataUrl ? (
                        <img 
                          src="/images/piece-placeholder.png" 
                          alt="No preview available"
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <img 
                          src="/images/piece-placeholder.png" 
                          alt="No preview available"
                          className="w-full h-full object-cover"
                        />
                      )}
                    </div>

                    {/* Status Badge */}
                    <div className="absolute top-3 left-3">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1 ${statusInfo.bg} ${statusInfo.color}`}>
                        {statusInfo.icon}
                        {statusInfo.label}
                      </span>
                    </div>

                    {/* Type Badge */}
                    <div className="absolute top-3 right-3">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        offer.type === 'received' ? 'bg-green-500 text-white' : 'bg-blue-500 text-white'
                      }`}>
                        {offer.type === 'received' ? 'Received' : 'Requested'}
                      </span>
                    </div>

                    {/* Time Left / Purchase Deadline */}
                    {offer.status === 'accepted' ? (
                      // Purchase deadline for accepted offers - minimal badge
                      <div className="absolute bottom-3 right-3">
                        <div className={`flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-medium backdrop-blur-md shadow-sm border ${
                          offer.timeLeft.includes('Expired') || offer.timeLeft.includes('hour') || offer.timeLeft.includes('minute')
                            ? 'bg-red-500/90 text-white border-red-400/50 animate-pulse'
                            : offer.timeLeft.includes('1 day') || offer.timeLeft.includes('0 day')
                            ? 'bg-orange-500/90 text-white border-orange-400/50'
                            : 'bg-blue-500/90 text-white border-blue-400/50'
                        }`}>
                          <Clock size={10} />
                          <span>{offer.timeLeft}</span>
                        </div>
                      </div>
                    ) : (
                      // Regular expiration for active offers
                      <div className="absolute bottom-3 right-3 flex items-center gap-1 bg-black/60 backdrop-blur-sm px-2 py-1 rounded-md text-[10px] text-white/90 shadow-sm">
                        <Clock size={9} />
                        {offer.timeLeft}
                      </div>
                    )}

                    {/* Simplified Piece Info */}
                    <div className="p-3 space-y-2">
                      {/* Title */}
                      <h3 className="text-white font-bold text-sm line-clamp-1 group-hover:text-gray-200 transition-colors">
                        {offer.name}
                      </h3>

                      {/* Price */}
                      <div className="space-y-0.5">
                        <div className="text-gray-400 text-xs">
                          Offered
                        </div>
                        <div className="text-green-400 font-semibold text-sm">
                          {offer.price} SOL
                        </div>
                        <div className="text-gray-500 text-xs">
                          ≈ ${(parseFloat(offer.price || 0) * solPrice).toFixed(2)} USD
                        </div>
                      </div>
                    </div>

                    {/* Hover Action Buttons - Slides up from bottom */}
                    {(offer.status === 'active' || offer.status === 'accepted') && (
                      <div className="absolute bottom-0 left-0 right-0 transform translate-y-full group-hover:translate-y-0 transition-all duration-300 ease-out p-3">
                        <div className="flex gap-1.5">
                          {offer.status === 'accepted' && offer.type === 'made' ? (
                            // Buyer can complete purchase for accepted offers
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleCompletePurchase(offer);
                              }}
                              disabled={isProcessing}
                              className="w-full bg-green-600 hover:bg-green-700 text-white py-2 px-2 rounded-lg font-semibold transition-all duration-200 text-xs flex items-center justify-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              <DollarSign size={12} />
                              {isProcessing ? 'Processing...' : 'Complete Purchase'}
                            </button>
                          ) : offer.status === 'active' && offer.type === 'received' ? (
                            // Actions for active received offers (offers made to you that are still active)
                            <>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleAcceptOffer(offer);
                                }}
                                className="flex-1 bg-green-600 hover:bg-green-700 text-white py-2 px-2 rounded-lg font-semibold transition-all duration-200 text-xs flex items-center justify-center gap-1"
                              >
                                <Check size={12} />
                                Accept
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeclineOffer(offer);
                                }}
                                className="flex-1 bg-red-600 hover:bg-red-700 text-white py-2 px-2 rounded-lg font-semibold transition-all duration-200 text-xs flex items-center justify-center gap-1"
                              >
                                <X size={12} />
                                Decline
                              </button>
                            </>
                          ) : offer.status === 'accepted' && offer.type === 'received' ? (
                            // Already accepted - show status message
                            <div className="w-full bg-green-600 text-white py-2.5 px-3 rounded-lg font-bold text-sm flex items-center justify-center gap-2 shadow-lg shadow-green-500/30">
                              <CheckCircle size={16} />
                              Accepted
                            </div>
                          ) : (
                            // Actions for made offers (offers you sent to others)
                            <>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleEditPrice(offer);
                                }}
                                className="flex-1 bg-blue-600 hover:bg-blue-700 text-white py-2 px-2 rounded-lg font-semibold transition-all duration-200 text-xs flex items-center justify-center gap-1"
                              >
                                <Edit size={12} />
                                Edit
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleCancelOffer(offer);
                                }}
                                className="flex-1 bg-red-600 hover:bg-red-700 text-white py-2 px-2 rounded-lg font-semibold transition-all duration-200 text-xs flex items-center justify-center gap-1"
                              >
                                <X size={12} />
                                Cancel
                              </button>
                            </>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-16">
              <div className="w-20 h-20 bg-[#2a2a2a] rounded-2xl flex items-center justify-center mx-auto mb-4">
                {activeTab === 'requested' && <Tag size={40} className="text-gray-400" />}
                {activeTab === 'incoming' && <TrendingUp size={40} className="text-gray-400" />}
                {activeTab === 'history' && <Clock size={40} className="text-gray-400" />}
              </div>
              <h3 className="text-xl font-medium text-gray-400 mb-2">
                {activeTab === 'requested' && 'No Offers Requested'}
                {activeTab === 'incoming' && 'No Incoming Offers'}
                {activeTab === 'history' && 'No Offers History'}
              </h3>
              <p className="text-gray-500 mb-6">
                {searchTerm ? 'Try adjusting your search' : (
                  <>
                    {activeTab === 'requested' && 'You haven\'t made any offers yet'}
                    {activeTab === 'incoming' && 'You haven\'t received any offers yet'}
                    {activeTab === 'history' && 'No completed, expired, or cancelled offers'}
                  </>
                )}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Accept Offer Modal */}
      {acceptModal.isOpen && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-[60]">
          <div className="bg-[#111111] border border-[#2a2a2a] rounded-2xl p-6 max-w-md w-full mx-4 relative">
            {/* Loading Overlay */}
            {isProcessing && (
              <div className="absolute inset-0 bg-black/95 rounded-2xl flex flex-col items-center justify-center z-10">
                <div className="w-16 h-16 border-4 border-green-500/30 border-t-green-500 rounded-full animate-spin mb-4"></div>
                <p className="text-white font-semibold text-lg mb-2">Accepting Offer...</p>
                <p className="text-gray-400 text-sm text-center px-6">
                  Please confirm the transaction in your wallet
                </p>
              </div>
            )}

            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-green-500/20 rounded-full flex items-center justify-center">
                <CheckCircle size={24} className="text-green-500" />
              </div>
              <div>
                <h3 className="text-xl font-semibold text-white">Accept Offer</h3>
                <p className="text-gray-400 text-sm">Are you sure you want to accept this offer?</p>
              </div>
            </div>
            
            <div className="mb-6">
              <div className="bg-[#0a0a0a] rounded-xl p-4">
                <h4 className="text-white font-medium mb-2">{acceptModal.offer?.name}</h4>
                <div className="text-gray-400 text-sm">
                  Offer: <span className="text-green-400 font-medium">{acceptModal.offer?.price} SOL</span>
                </div>
                <div className="text-gray-400 text-sm">
                  Floor: {acceptModal.offer?.floorPrice} SOL
                </div>
              </div>
              <div className="mt-4 text-gray-300 text-sm">
                This will complete the transaction and transfer the piece to the buyer.
              </div>
            </div>
            
            <div className="flex gap-3">
              <button
                onClick={() => setAcceptModal({ isOpen: false, offer: null })}
                disabled={isProcessing}
                className="flex-1 px-4 py-3 bg-[#2a2a2a] hover:bg-[#3a3a3a] text-white rounded-xl transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                onClick={confirmAcceptOffer}
                disabled={isProcessing}
                className="flex-1 px-4 py-3 bg-green-600 hover:bg-green-700 text-white rounded-xl transition-colors duration-200 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isProcessing ? 'Processing...' : 'Accept Offer'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Decline Offer Modal */}
      {declineModal.isOpen && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-[60]">
          <div className="bg-[#111111] border border-[#2a2a2a] rounded-2xl p-6 max-w-md w-full mx-4">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-red-500/20 rounded-full flex items-center justify-center">
                <XCircle size={24} className="text-red-500" />
              </div>
              <div>
                <h3 className="text-xl font-semibold text-white">Decline Offer</h3>
                <p className="text-gray-400 text-sm">Are you sure you want to decline this offer?</p>
              </div>
            </div>
            
            <div className="mb-6">
              <div className="bg-[#0a0a0a] rounded-xl p-4">
                <h4 className="text-white font-medium mb-2">{declineModal.offer?.name}</h4>
                <div className="text-gray-400 text-sm">
                  Offer: <span className="text-green-400 font-medium">{declineModal.offer?.price} SOL</span>
                </div>
              </div>
              <div className="mt-4 text-gray-300 text-sm">
                This will reject the offer and notify the buyer.
              </div>
            </div>
            
            <div className="flex gap-3">
              <button
                onClick={() => setDeclineModal({ isOpen: false, offer: null })}
                className="flex-1 px-4 py-3 bg-[#2a2a2a] hover:bg-[#3a3a3a] text-white rounded-xl transition-colors duration-200"
              >
                Cancel
              </button>
              <button
                onClick={confirmDeclineOffer}
                className="flex-1 px-4 py-3 bg-red-600 hover:bg-red-700 text-white rounded-xl transition-colors duration-200 font-medium"
              >
                Decline Offer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Price Modal */}
      {editPriceModal.isOpen && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-[60]">
          <div className="bg-[#111111] border border-[#2a2a2a] rounded-2xl p-6 max-w-md w-full mx-4">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-blue-500/20 rounded-full flex items-center justify-center">
                <Edit size={24} className="text-blue-500" />
              </div>
              <div>
                <h3 className="text-xl font-semibold text-white">Edit Offer Price</h3>
                <p className="text-gray-400 text-sm">Update the price for your offer</p>
              </div>
            </div>
            
            <div className="mb-6">
              <div className="bg-[#0a0a0a] rounded-xl p-4 mb-4">
                <h4 className="text-white font-medium mb-2">{editPriceModal.offer?.name}</h4>
                <div className="text-gray-400 text-sm">
                  Current Price: <span className="text-green-400 font-medium">{editPriceModal.offer?.price} SOL</span>
                </div>
                <div className="text-gray-400 text-sm">
                  Piece Price: {editPriceModal.offer?.floorPrice} SOL
                </div>
              </div>
              
              <div className="space-y-2">
                <label className="text-white text-sm font-medium">New Offer Price (SOL)</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={editPriceModal.newPrice}
                  onChange={(e) => setEditPriceModal(prev => ({ ...prev, newPrice: e.target.value }))}
                  className="w-full px-4 py-3 bg-[#0a0a0a] border border-[#2a2a2a] rounded-xl text-white placeholder-gray-400 focus:outline-none focus:border-white"
                  placeholder="Enter new price"
                />
                <div className="text-gray-500 text-sm">
                  ≈ ${(parseFloat(editPriceModal.newPrice || 0) * solPrice).toFixed(2)} USD
                </div>
              </div>
            </div>
            
            <div className="flex gap-3">
              <button
                onClick={() => setEditPriceModal({ isOpen: false, offer: null, newPrice: '' })}
                className="flex-1 px-4 py-3 bg-[#2a2a2a] hover:bg-[#3a3a3a] text-white rounded-xl transition-colors duration-200 font-medium"
              >
                Cancel
              </button>
              <button
                onClick={confirmEditPrice}
                className="flex-1 px-4 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl transition-colors duration-200 font-medium"
              >
                Update Price
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Cancel Offer Modal */}
      {cancelOfferModal.isOpen && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-[60]">
          <div className="bg-[#111111] border border-[#2a2a2a] rounded-2xl p-6 max-w-md w-full mx-4">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-red-500/20 rounded-full flex items-center justify-center">
                <XCircle size={24} className="text-red-500" />
              </div>
              <div>
                <h3 className="text-xl font-semibold text-white">Cancel Offer</h3>
                <p className="text-gray-400 text-sm">Are you sure you want to cancel this offer?</p>
              </div>
            </div>
            
            <div className="mb-6">
              <div className="bg-[#0a0a0a] rounded-xl p-4">
                <h4 className="text-white font-medium mb-2">{cancelOfferModal.offer?.name}</h4>
                <div className="text-gray-400 text-sm">
                  Offer Price: <span className="text-green-400 font-medium">{cancelOfferModal.offer?.price} SOL</span>
                </div>
                <div className="text-gray-400 text-sm">
                  To: {cancelOfferModal.offer?.offerTo}
                </div>
              </div>
              <div className="mt-4 text-gray-300 text-sm">
                This will cancel your offer and notify the recipient.
              </div>
            </div>
            
            <div className="flex gap-3">
              <button
                onClick={() => setCancelOfferModal({ isOpen: false, offer: null })}
                className="flex-1 px-4 py-3 bg-[#2a2a2a] hover:bg-[#3a3a3a] text-white rounded-xl transition-colors duration-200 font-medium"
              >
                Keep Offer
              </button>
              <button
                onClick={confirmCancelOffer}
                className="flex-1 px-4 py-3 bg-red-600 hover:bg-red-700 text-white rounded-xl transition-colors duration-200 font-medium"
              >
                Cancel Offer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirm Purchase Modal */}
      {confirmPurchaseModal.isOpen && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-[60]">
          <div className="bg-[#111111] border border-[#2a2a2a] rounded-2xl p-6 max-w-md w-full mx-4 relative">
            {/* Loading Overlay */}
            {isProcessing && (
              <div className="absolute inset-0 bg-black/95 rounded-2xl flex flex-col items-center justify-center z-10">
                <div className="w-16 h-16 border-4 border-green-500/30 border-t-green-500 rounded-full animate-spin mb-4"></div>
                <p className="text-white font-semibold text-lg mb-2">Processing Purchase...</p>
                <p className="text-gray-400 text-sm text-center px-6">
                  Please confirm the transaction in your wallet
                </p>
              </div>
            )}

            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-green-500/20 rounded-full flex items-center justify-center">
                <DollarSign size={24} className="text-green-500" />
              </div>
              <div>
                <h3 className="text-xl font-semibold text-white">Complete Purchase</h3>
                <p className="text-gray-400 text-sm">Confirm your purchase details</p>
              </div>
            </div>
            
            <div className="mb-6">
              <div className="bg-[#0a0a0a] rounded-xl p-4 mb-4">
                <h4 className="text-white font-medium mb-3">{confirmPurchaseModal.offer?.name}</h4>
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-400">Purchase Price</span>
                    <span className="text-white font-medium">{confirmPurchaseModal.offer?.price} SOL</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-400">Platform Fee (2.5%)</span>
                    <span className="text-white">{(parseFloat(confirmPurchaseModal.offer?.price || 0) * 0.025).toFixed(4)} SOL</span>
                  </div>
                  <div className="border-t border-gray-700 pt-2 mt-2"></div>
                  <div className="flex justify-between text-sm font-semibold">
                    <span className="text-white">Total Cost</span>
                    <span className="text-green-400">{confirmPurchaseModal.offer?.price} SOL</span>
                  </div>
                </div>
              </div>
              <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-3">
                <p className="text-blue-300 text-xs">
                  ✓ Piece will be transferred to your wallet<br />
                  ✓ Seller receives payment after fees
                </p>
              </div>
            </div>
            
            <div className="flex gap-3">
              <button
                onClick={() => setConfirmPurchaseModal({ isOpen: false, offer: null })}
                disabled={isProcessing}
                className="flex-1 px-4 py-3 bg-[#2a2a2a] hover:bg-[#3a3a3a] text-white rounded-xl transition-colors duration-200 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                onClick={confirmCompletePurchase}
                disabled={isProcessing}
                className="flex-1 px-4 py-3 bg-green-600 hover:bg-green-700 text-white rounded-xl transition-colors duration-200 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isProcessing ? 'Processing...' : 'Confirm Purchase'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* View Details Modal */}
      {viewDetailsModal.isOpen && (
        <div 
          className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4"
          onClick={closeViewDetailsModal}
        >
          <div 
            className="bg-[#111111] border border-[#2a2a2a] rounded-2xl max-w-5xl w-full max-h-[90vh] flex flex-col relative"
            onClick={(e) => e.stopPropagation()}
            style={{ overflow: 'visible' }}
          >
            <div className="flex flex-1 overflow-hidden rounded-2xl" style={{ position: 'relative', zIndex: 1 }}>
              {/* Left side - Thumbnail/3D Model */}
              <div className="w-1/2 p-6 overflow-y-auto">
                {/* 3D Model Toggle Buttons */}
                {(() => {
                  const normalizedPiece = viewDetailsModal.offer ? normalizePieces([viewDetailsModal.offer])[0] : null;
                  const has3DModel = normalizedPiece?.previewUrl && (normalizedPiece.previewUrl.includes('.glb') || normalizedPiece.previewUrl.includes('.gltf'));
                  
                  if (has3DModel) {
                    return (
                      <div className="flex mb-4 bg-[#111111] rounded-xl p-1">
                        <button
                          onClick={() => setModalViewMode('3d')}
                          className={`flex-1 py-2.5 px-3 rounded-xl text-sm font-medium transition-all duration-200  ${
                            modalViewMode === '3d'
                              ? 'text-white bg-[#2a2a2a] shadow-lg' 
                              : 'text-gray-400 hover:text-white hover:bg-[#0a0a0a]'
                          }`}
                        >
                          3D Model
                        </button>
                        <button
                          onClick={() => setModalViewMode('thumbnail')}
                          className={`flex-1 py-2.5 px-3 rounded-xl text-sm font-medium transition-all duration-200  ${
                            modalViewMode === 'thumbnail'
                              ? 'text-white bg-[#2a2a2a] shadow-lg' 
                              : 'text-gray-400 hover:text-white hover:bg-[#0a0a0a]'
                          }`}
                        >
                          Thumbnail
                        </button>
                      </div>
                    );
                  }
                  return null;
                })()}
              
                <div className="rounded-xl flex items-center justify-center mb-1 overflow-hidden aspect-square bg-[#1a1a1a]">
                  {(() => {
                    const normalizedPiece = viewDetailsModal.offer ? normalizePieces([viewDetailsModal.offer])[0] : null;
                    
                    if (modalViewMode === '3d') {
                      const modelBucket = viewDetailsModal.offer?.preview_bucket || viewDetailsModal.offer?.model_bucket;
                      const modelKey = viewDetailsModal.offer?.preview_key || viewDetailsModal.offer?.model_key;
                      const fallbackSrc = normalizedPiece?.previewUrl || normalizedPiece?.modelUrl;
                      
                      if (modelBucket && modelKey) {
                        const customOrientation = viewDetailsModal.offer?.customOrientation || null;
                        
                        return (
                          <ModelViewer
                            modelBucket={modelBucket}
                            modelKey={modelKey}
                            posterUrl={normalizedPiece?.posterUrl || normalizedPiece?.thumbnailUrl || '/images/piece-placeholder.png'}
                            orientation={customOrientation}
                            className="w-full h-full modal"
                            onLoad={() => {
                              console.log('✅ Modal: 3D model loaded successfully');
                              setThreeDStatus('loaded');
                            }}
                            onError={(error) => {
                              console.error('❌ Modal: 3D model error:', error);
                              setThreeDStatus('error');
                              setThreeDError(error.message || 'Failed to load 3D model');
                            }}
                          />
                        );
                      } else if (fallbackSrc) {
                        const customOrientation = viewDetailsModal.offer?.customOrientation || null;
                        
                        return (
                          <ModelViewer
                            src={fallbackSrc}
                            posterUrl={normalizedPiece?.posterUrl || normalizedPiece?.thumbnailUrl || '/images/piece-placeholder.png'}
                            orientation={customOrientation}
                            className="w-full h-full modal"
                            onLoad={() => {
                              console.log('✅ Modal: 3D model loaded successfully');
                              setThreeDStatus('loaded');
                            }}
                            onError={(error) => {
                              console.error('❌ Modal: 3D model error:', error);
                              setThreeDStatus('error');
                              setThreeDError(error.message || 'Failed to load 3D model');
                            }}
                          />
                        );
                      } else {
                        return (
                          <div className="w-full h-full flex items-center justify-center bg-gray-800 rounded-xl">
                            <div className="text-center text-white p-6">
                              <div className="text-6xl mb-4">⚠️</div>
                              <div className="text-xl font-semibold mb-2">No 3D model available</div>
                              <div className="text-sm text-gray-400">This piece doesn't have a 3D model</div>
                            </div>
                          </div>
                        );
                      }
                    } else {
                      // Show thumbnail image
                      return viewDetailsModal.offer?.thumbnailUrl ? (
                        <img 
                          src={viewDetailsModal.offer.thumbnailUrl} 
                          alt={viewDetailsModal.offer.name}
                          className="w-full h-full object-cover rounded-xl"
                          onError={(e) => {
                            console.log('Modal thumbnail load error for:', viewDetailsModal.offer.name, 'URL:', viewDetailsModal.offer.thumbnailUrl);
                            e.target.style.display = 'none';
                          }}
                        />
                      ) : viewDetailsModal.offer?.thumbnail?.dataUrl ? (
                        <img 
                          src={viewDetailsModal.offer.thumbnail.dataUrl} 
                          alt={viewDetailsModal.offer.name}
                          className="w-full h-full object-cover rounded-xl"
                        />
                      ) : (
                        <img 
                          src="/images/piece-placeholder.png" 
                          alt="No preview available"
                          className="w-full h-full object-cover"
                        />
                      );
                    }
                  })()}
                </div>

                {/* Creator and Offer Information Section */}
                <div className="space-y-4 mb-6">
                  {/* Original Creator */}
                  <div>
                    <div className="text-gray-400 text-xs font-semibold uppercase tracking-wider mb-2 px-1">
                      Original Creator
                    </div>
                    <button 
                      onClick={() => {
                        const creatorWallet = viewDetailsModal.offer?.originalCreator?.wallet || viewDetailsModal.offer?.creator?.wallet;
                        if (creatorWallet) {
                          navigate(`/creator/${creatorWallet}`);
                          closeViewDetailsModal();
                        }
                      }}
                      className="w-full flex items-center gap-3 p-3 bg-[#0a0a0a] hover:bg-[#1a1a1a] rounded-xl transition-colors group border border-[#1a1a1a]"
                    >
                      <div className="w-10 h-10 rounded-full flex items-center justify-center overflow-hidden flex-shrink-0">
                        {(viewDetailsModal.offer?.originalCreator?.profilePicture || viewDetailsModal.offer?.creator?.profilePicture || viewDetailsModal.offer?.originalCreator?.profile_picture_url || viewDetailsModal.offer?.creator?.profile_picture_url) ? (
                          <img
                            src={viewDetailsModal.offer?.originalCreator?.profilePicture || viewDetailsModal.offer?.creator?.profilePicture || viewDetailsModal.offer?.originalCreator?.profile_picture_url || viewDetailsModal.offer?.creator?.profile_picture_url}
                            alt={viewDetailsModal.offer?.originalCreator?.username || viewDetailsModal.offer?.creator?.username || 'Creator'}
                            className="w-full h-full object-cover rounded-full"
                          />
                        ) : (
                          <div className="w-full h-full bg-gradient-to-br from-green-500 to-blue-500 rounded-full flex items-center justify-center">
                            <span className="text-white font-bold text-sm">
                              {(viewDetailsModal.offer?.originalCreator?.username || viewDetailsModal.offer?.creator?.username || 'Creator').charAt(0)}
                            </span>
                          </div>
                        )}
                      </div>
                      <div className="flex-1 text-left min-w-0">
                        <div className="flex items-center gap-1.5">
                          <div className="text-white font-medium text-sm group-hover:text-gray-200 transition-colors truncate">
                            {viewDetailsModal.offer?.originalCreator?.username || viewDetailsModal.offer?.creator?.username || 'Original Creator'}
                          </div>
                          {(viewDetailsModal.offer?.originalCreator?.verified || viewDetailsModal.offer?.creator?.verified) && (
                            <VerifiedBadge 
                              size="sm"
                              verificationType={viewDetailsModal.offer?.originalCreator?.verificationType || viewDetailsModal.offer?.creator?.verificationType || 'none'}
                            />
                          )}
                        </div>
                        <div className="text-gray-400 text-xs mt-0.5">
                          {viewDetailsModal.offer?.originalCreator?.piecesCount || viewDetailsModal.offer?.creator?.piecesCount || '0'} pieces created
                        </div>
                      </div>
                      <div className="text-gray-400 group-hover:text-white transition-colors flex-shrink-0">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M9 18l6-6-6-6"/>
                        </svg>
                      </div>
                    </button>

                    {/* Creator Royalty */}
                    {viewDetailsModal.offer?.royalty_bps !== undefined && viewDetailsModal.offer?.royalty_bps !== null && (
                      <div className="mt-3 px-1">
                        <span className="inline-block px-2 py-0.5 bg-green-500/10 border border-green-500/30 rounded-md text-green-400 text-xs">
                          {(viewDetailsModal.offer.royalty_bps / 100).toFixed(1)}% Creator Royalty
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Offer From/To */}
                  <div>
                    <div className="text-gray-400 text-xs font-semibold uppercase tracking-wider mb-2 px-1">
                      {viewDetailsModal.offer?.type === 'received' ? 'Offer From' : 'Offer To'}
                    </div>
                    <button 
                      onClick={() => {
                        const targetWallet = viewDetailsModal.offer?.type === 'received' 
                          ? viewDetailsModal.offer?.offerer_wallet 
                          : viewDetailsModal.offer?.piece_owner_wallet;
                        if (targetWallet) {
                          navigate(`/creator/${targetWallet}`);
                          closeViewDetailsModal();
                        }
                      }}
                      className="w-full flex items-center gap-3 p-3 bg-[#0a0a0a] hover:bg-[#1a1a1a] rounded-xl transition-colors group border border-[#1a1a1a]"
                    >
                      <div className="w-10 h-10 rounded-full flex items-center justify-center overflow-hidden flex-shrink-0">
                        {(() => {
                          const profile = viewDetailsModal.offer?.type === 'received' 
                            ? viewDetailsModal.offer?.offererProfile 
                            : viewDetailsModal.offer?.ownerProfile;
                          const profilePicture = profile?.profilePicture || profile?.profile_picture_url;
                          const username = profile?.username || (viewDetailsModal.offer?.type === 'received' ? viewDetailsModal.offer?.offerFrom : viewDetailsModal.offer?.offerTo) || 'User';
                          
                          return profilePicture ? (
                            <img
                              src={profilePicture}
                              alt={username}
                              className="w-full h-full object-cover rounded-full"
                            />
                          ) : (
                            <div className="w-full h-full bg-gradient-to-br from-purple-500 to-pink-500 rounded-full flex items-center justify-center">
                              <span className="text-white font-bold text-sm">
                                {username.charAt(0).toUpperCase()}
                              </span>
                            </div>
                          );
                        })()}
                      </div>
                      <div className="flex-1 text-left min-w-0">
                        <div className="flex items-center gap-1.5">
                          <div className="text-white font-medium text-sm group-hover:text-gray-200 transition-colors truncate">
                            {(() => {
                              const profile = viewDetailsModal.offer?.type === 'received' 
                                ? viewDetailsModal.offer?.offererProfile 
                                : viewDetailsModal.offer?.ownerProfile;
                              return profile?.username || (viewDetailsModal.offer?.type === 'received' ? viewDetailsModal.offer?.offerFrom : viewDetailsModal.offer?.offerTo) || 'User';
                            })()}
                          </div>
                          {(() => {
                            const profile = viewDetailsModal.offer?.type === 'received' 
                              ? viewDetailsModal.offer?.offererProfile 
                              : viewDetailsModal.offer?.ownerProfile;
                            return profile?.verified && (
                              <VerifiedBadge 
                                size="sm"
                                verificationType={profile?.verificationType || 'none'}
                              />
                            );
                          })()}
                        </div>
                        <div className="text-gray-400 text-xs mt-0.5">
                          {(() => {
                            const profile = viewDetailsModal.offer?.type === 'received' 
                              ? viewDetailsModal.offer?.offererProfile 
                              : viewDetailsModal.offer?.ownerProfile;
                            return `${profile?.piecesCount || '0'} pieces created`;
                          })()}
                        </div>
                      </div>
                      <div className="text-gray-400 group-hover:text-white transition-colors flex-shrink-0">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M9 18l6-6-6-6"/>
                        </svg>
                      </div>
                    </button>
                  </div>
                </div>
              </div>

              {/* Right side - Details */}
              <div className="w-1/2 p-6 border-l border-[#2a2a2a] overflow-y-auto">
                <div className="flex justify-between items-start mb-6">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h2 className="text-2xl font-bold text-white">
                        {viewDetailsModal.offer?.name}
                      </h2>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleFavorite(viewDetailsModal.offer.piece_id);
                        }}
                        className="p-1.5 transition-all duration-300 hover:scale-110 active:scale-95"
                        title={isFavorited(viewDetailsModal.offer.piece_id) ? "Remove from favorites" : "Add to favorites"}
                      >
                        <Heart 
                          size={20} 
                          className={`transition-all duration-300 ${
                            isFavorited(viewDetailsModal.offer.piece_id)
                              ? 'text-red-500 fill-red-500'
                              : 'text-gray-400 hover:text-red-500'
                          }`} 
                        />
                      </button>
                    </div>
                    {/* Tags */}
                    <div className="flex items-center gap-2 flex-wrap">
                      {viewDetailsModal.offer?.piece_tags && viewDetailsModal.offer.piece_tags.length > 0 ? (
                        viewDetailsModal.offer.piece_tags.map((tag, index) => (
                          <span 
                            key={index}
                            className="bg-gray-600 text-gray-200 px-3 py-1 rounded-full text-sm font-medium"
                          >
                            {tag}
                          </span>
                        ))
                      ) : (
                        <span className="text-gray-400 text-sm">No tags</span>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={() => setActivityPanelOpen(!activityPanelOpen)}
                    className={`px-4 py-2 rounded-lg font-medium text-sm transition-all duration-200 ${
                      activityPanelOpen 
                        ? 'bg-[#2a2a2a] text-white hover:bg-[#3a3a3a] border border-[#3a3a3a]' 
                        : 'bg-[#1a1a1a] text-gray-300 hover:bg-[#2a2a2a] hover:text-white border border-[#2a2a2a]'
                    }`}
                  >
                    Activity
                  </button>
                </div>

                <div className="space-y-5">
                  {/* PIECE INFORMATION SECTION */}
                  
                  {/* Category and Color */}
                  <div className="bg-[#0a0a0a] rounded-xl p-4 border border-[#1a1a1a]">
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 bg-white rounded-full"></div>
                        <span className="text-gray-300 text-sm font-medium">
                          {getCategoryDisplayName(viewDetailsModal.offer?.category) || 'Uncategorized'}
                        </span>
                      </div>
                      {viewDetailsModal.offer?.piece_color && (
                        <div className="text-gray-300 text-sm">
                          <span className="text-gray-500">Color: </span>
                          <span className="capitalize font-medium">{viewDetailsModal.offer.piece_color}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Piece Description */}
                  <div className="bg-[#0a0a0a] rounded-xl p-4 border border-[#1a1a1a]">
                    <h3 className="text-white font-semibold mb-2 text-sm">Description</h3>
                    <p className="text-gray-400 text-sm leading-relaxed">
                      {viewDetailsModal.offer?.piece_description || 'A premium digital fashion piece with modern styling and high-quality design.'}
                    </p>
                  </div>

                  {/* Piece Price */}
                  <div className="bg-[#0a0a0a] rounded-xl p-4 border border-[#1a1a1a]">
                    <h3 className="text-white font-semibold mb-3 text-sm">Pricing</h3>
                    <div className="space-y-1">
                      <div className="flex items-center justify-between">
                        <span className="text-gray-400 text-sm">Price</span>
                        <div className="text-green-400 font-bold text-xl">
                          {viewDetailsModal.offer?.piece_listing_price || viewDetailsModal.offer?.piece_price || viewDetailsModal.offer?.floorPrice} SOL
                        </div>
                      </div>
                      <div className="text-right text-gray-500 text-sm">
                        ≈ ${((parseFloat(viewDetailsModal.offer?.piece_listing_price || viewDetailsModal.offer?.piece_price || viewDetailsModal.offer?.floorPrice || 0)) * solPrice).toFixed(2)} USD
                      </div>
                    </div>
                  </div>

                  {/* Scarcity */}
                  <div className="bg-[#0a0a0a] rounded-xl p-4 border border-[#1a1a1a]">
                    <div className="text-gray-400 text-sm mb-1">Scarcity</div>
                    <div className="text-white font-semibold text-lg">
                      {viewDetailsModal.offer?.piece_quantity || 1}
                    </div>
                  </div>

                  {/* Dropped Date */}
                  {viewDetailsModal.offer?.piece_created_at && (
                    <div className="text-gray-500 text-sm pt-2">
                      Dropped {new Date(viewDetailsModal.offer.piece_created_at).toLocaleDateString('en-US', { 
                        year: '2-digit', 
                        month: '2-digit', 
                        day: '2-digit' 
                      })}
                    </div>
                  )}

                  {/* OFFER INFORMATION SECTION */}
                  <div className="border-t border-[#2a2a2a] pt-5 mt-5">
                    <h2 className="text-white font-bold text-lg mb-4">Offer Details</h2>
                    
                    {/* Status Badges */}
                    <div className="flex items-center gap-2 flex-wrap mb-4">
                      {(() => {
                        const statusInfo = getStatusInfo(viewDetailsModal.offer?.status);
                        return (
                          <span className={`px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1 ${statusInfo.bg} ${statusInfo.color}`}>
                            {statusInfo.icon}
                            {statusInfo.label}
                          </span>
                        );
                      })()}
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        viewDetailsModal.offer?.type === 'received' ? 'bg-green-500 text-white' : 'bg-blue-500 text-white'
                      }`}>
                        {viewDetailsModal.offer?.type === 'received' ? 'Received' : 'Requested'}
                      </span>
                    </div>

                    {/* Offer Price */}
                    <div className="bg-[#0a0a0a] rounded-xl p-4 border border-[#1a1a1a] mb-4">
                      <h3 className="text-white font-semibold mb-3 text-sm">Offer Price</h3>
                      <div className="space-y-1">
                        <div className="flex items-center justify-between">
                          <span className="text-gray-400 text-sm">Offered</span>
                          <div className="text-green-400 font-bold text-xl">
                            {viewDetailsModal.offer?.price} SOL
                          </div>
                        </div>
                        <div className="text-right text-gray-500 text-sm">
                          ≈ ${(parseFloat(viewDetailsModal.offer?.price || 0) * solPrice).toFixed(2)} USD
                        </div>
                      </div>
                    </div>

                    {/* Offer Information */}
                    <div className="bg-[#0a0a0a] rounded-xl p-4 border border-[#1a1a1a]">
                      <h3 className="text-white font-semibold mb-3 text-sm">Offer Information</h3>
                      <div className="space-y-2">
                        {/* Time Left / Purchase Deadline */}
                        <div className="flex items-center justify-between">
                          <span className="text-gray-400 text-sm">
                            {viewDetailsModal.offer?.status === 'accepted' ? 'Purchase Deadline' : 'Time Left'}
                          </span>
                          <div className={`text-sm font-medium ${
                            viewDetailsModal.offer?.status === 'accepted'
                              ? (viewDetailsModal.offer?.timeLeft.includes('Expired') || 
                                 viewDetailsModal.offer?.timeLeft.includes('hour') || 
                                 viewDetailsModal.offer?.timeLeft.includes('minute'))
                                ? 'text-red-400 font-bold'
                                : (viewDetailsModal.offer?.timeLeft.includes('1 day') || 
                                   viewDetailsModal.offer?.timeLeft.includes('0 day'))
                                ? 'text-orange-400 font-semibold'
                                : 'text-blue-400'
                              : 'text-white'
                          }`}>
                            {viewDetailsModal.offer?.timeLeft}
                          </div>
                        </div>
                        
                        {/* Urgent warning for accepted offers with less than 24 hours */}
                        {viewDetailsModal.offer?.status === 'accepted' && 
                         (viewDetailsModal.offer?.timeLeft.includes('hour') || 
                          viewDetailsModal.offer?.timeLeft.includes('minute') ||
                          viewDetailsModal.offer?.timeLeft.includes('1 day') ||
                          viewDetailsModal.offer?.timeLeft.includes('0 day')) && (
                          <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-2">
                            <p className="text-red-400 text-xs font-medium flex items-center gap-1">
                              <AlertCircle size={12} />
                              {viewDetailsModal.offer?.type === 'made' 
                                ? 'Complete purchase soon or offer will be declined!' 
                                : 'Buyer must complete purchase soon or offer will be auto-declined!'}
                            </p>
                          </div>
                        )}
                        
                        {viewDetailsModal.offer?.message && (
                          <div className="pt-2 border-t border-[#1a1a1a]">
                            <span className="text-gray-400 text-sm">Message</span>
                            <p className="text-white text-sm mt-1">{viewDetailsModal.offer.message}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="space-y-3 pt-4 pb-2">
                    {(viewDetailsModal.offer?.status === 'active' || viewDetailsModal.offer?.status === 'accepted') && (
                      <>
                        {viewDetailsModal.offer?.status === 'accepted' && viewDetailsModal.offer?.type === 'made' ? (
                          // Buyer can complete purchase for accepted offers
                          <button 
                            onClick={() => handleCompletePurchase(viewDetailsModal.offer)}
                            disabled={isProcessing}
                            className="w-full px-4 py-3.5 bg-green-600 hover:bg-green-700 text-white rounded-xl transition-all duration-200 font-semibold text-sm shadow-lg hover:shadow-green-500/25 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                          >
                            <DollarSign size={16} />
                            {isProcessing ? 'Processing...' : `Complete Purchase - ${viewDetailsModal.offer?.price} SOL`}
                          </button>
                        ) : viewDetailsModal.offer?.status === 'active' && viewDetailsModal.offer?.type === 'received' ? (
                          // Actions for active received offers (offers made to you that are still active)
                          <div className="flex gap-3">
                            <button 
                              onClick={() => handleAcceptOffer(viewDetailsModal.offer)}
                              className="flex-1 px-4 py-3.5 bg-green-600 hover:bg-green-700 text-white rounded-xl transition-all duration-200 font-semibold text-sm shadow-lg hover:shadow-green-500/25 flex items-center justify-center gap-2"
                            >
                              <Check size={16} />
                              Accept Offer
                            </button>
                            <button 
                              onClick={() => handleDeclineOffer(viewDetailsModal.offer)}
                              className="flex-1 px-4 py-3.5 bg-red-600 hover:bg-red-700 text-white rounded-xl transition-all duration-200 font-semibold text-sm shadow-lg flex items-center justify-center gap-2"
                            >
                              <X size={16} />
                              Decline
                            </button>
                          </div>
                        ) : viewDetailsModal.offer?.status === 'accepted' && viewDetailsModal.offer?.type === 'received' ? (
                          // Already accepted - show status message
                          <div className="w-full px-4 py-3.5 bg-green-600/20 border border-green-600/50 text-green-400 rounded-xl font-semibold text-sm flex items-center justify-center gap-2">
                            <CheckCircle size={16} />
                            Offer Accepted - Awaiting Buyer to Complete Purchase
                          </div>
                        ) : (
                          // Actions for made offers (offers you sent to others)
                          <div className="flex gap-3">
                            <button 
                              onClick={() => handleEditPrice(viewDetailsModal.offer)}
                              className="flex-1 px-4 py-3.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl transition-all duration-200 font-semibold text-sm shadow-lg hover:shadow-blue-500/25 flex items-center justify-center gap-2"
                            >
                              <Edit size={16} />
                              Edit Price
                            </button>
                            <button 
                              onClick={() => handleCancelOffer(viewDetailsModal.offer)}
                              className="flex-1 px-4 py-3.5 bg-red-600 hover:bg-red-700 text-white rounded-xl transition-all duration-200 font-semibold text-sm shadow-lg flex items-center justify-center gap-2"
                            >
                              <X size={16} />
                              Cancel
                            </button>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Activity Side Panel - slides out from behind modal on desktop, overlay on mobile */}
            <div 
              className="absolute top-0 left-0 md:left-full h-full w-full md:w-80 bg-[#0a0a0a] border-l border-[#2a2a2a] md:rounded-r-2xl transition-all duration-300 ease-in-out shadow-2xl"
              style={{ 
                transform: activityPanelOpen ? 'translateX(0)' : (window.innerWidth < 768 ? 'translateX(-100%)' : 'translateX(-100%)'),
                zIndex: activityPanelOpen ? 50 : 0,
                opacity: activityPanelOpen ? 1 : 0,
                visibility: activityPanelOpen ? 'visible' : 'hidden',
                pointerEvents: activityPanelOpen ? 'auto' : 'none'
              }}
            >
              <div className="h-full flex flex-col p-5">
                <div className="flex items-center justify-between mb-4 pb-3 border-b border-[#2a2a2a]">
                  <h3 className="text-base font-bold text-white">Activity</h3>
                  <button
                    onClick={() => setActivityPanelOpen(false)}
                    className="p-1.5 hover:bg-[#1a1a1a] rounded-lg transition-colors"
                    title="Close Activity Panel"
                  >
                    <ChevronRight size={18} className="text-gray-400" />
                  </button>
                </div>
                <div className="flex-1 overflow-y-auto">
                  <PieceActivityAndHolders
                    pieceId={viewDetailsModal.offer?.piece_id}
                    pieceName={viewDetailsModal.offer?.name}
                    basePrice={parseFloat(viewDetailsModal.offer?.price) || 0}
                    solPrice={solPrice || 150}
                    totalQuantity={viewDetailsModal.offer?.quantity || 1}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Notification Toast */}
      {notification.show && (
        <div className="fixed top-4 right-4 z-[70] animate-slide-down">
          <div className={`${
            notification.type === 'success' ? 'bg-green-500/20 border-green-500/50' :
            notification.type === 'error' ? 'bg-red-500/20 border-red-500/50' :
            'bg-blue-500/20 border-blue-500/50'
          } border rounded-xl p-4 shadow-xl max-w-md`}>
            <div className="flex items-start gap-3">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                notification.type === 'success' ? 'bg-green-500/20' :
                notification.type === 'error' ? 'bg-red-500/20' :
                'bg-blue-500/20'
              }`}>
                {notification.type === 'success' ? (
                  <CheckCircle size={20} className="text-green-400" />
                ) : notification.type === 'error' ? (
                  <XCircle size={20} className="text-red-400" />
                ) : (
                  <AlertCircle size={20} className="text-blue-400" />
                )}
              </div>
              <div className="flex-1">
                <p className="text-white text-sm">{notification.message}</p>
              </div>
              <button
                onClick={() => setNotification({ ...notification, show: false })}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <X size={18} />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
    </WalletRequired>
  );
};

export default Offers; 
